import { useState } from 'react';
import { CircleBackslashIcon } from '@radix-ui/react-icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CopyUserId } from '@/components/bans/CopyUserId';
import { toFullS3Url } from '@/utils/toFullS3Url';
import { Badge } from '@/components/ui/badge';

export function BanPersonDialog({
  open,
  onOpenChange,
  name,
  userId,
  photoUrl,
  submissionCount,
  loading,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  userId: string;
  photoUrl?: string | null;
  submissionCount: number;
  loading?: boolean;
  onConfirm: (input: {
    hideExisting: boolean;
    reason: string;
  }) => Promise<void>;
}) {
  const [hideExisting, setHideExisting] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const isBusy = loading || busy;
  const displayName = name || 'this person';

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm({ hideExisting, reason });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-name="BAN_PERSON_DIALOG">
        <DialogHeader>
          <DialogTitle>Ban {displayName}?</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 text-left">
              <p className="flex items-center gap-2 font-medium text-destructive">
                <CircleBackslashIcon className="h-4 w-4" />
                Banned from all your screens
              </p>
              <p>
                They can&apos;t submit or vote on any of your screens, including
                future ones. They won&apos;t be told they&apos;re banned; on
                their phone, submissions will just show as unavailable.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg border bg-card p-3">
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img
                src={toFullS3Url(photoUrl)}
                alt=""
                className="h-10 w-10 rounded-md object-cover blur-[2px]"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-semibold">
                {displayName.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-medium">{displayName}</div>
              <CopyUserId id={userId} />
            </div>
          </div>
          <Badge variant="secondary">
            {submissionCount} submission{submissionCount === 1 ? '' : 's'}
          </Badge>
        </div>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={hideExisting}
            onCheckedChange={(value) => setHideExisting(value === true)}
          />
          <span>
            <span className="font-medium">
              Also hide their existing submissions ({submissionCount})
            </span>
            <span className="mt-1 block text-muted-foreground">
              Takes them off the Stage. If you unban them later, these go back
              to your approval queue.
            </span>
          </span>
        </label>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Reason (optional, only admins see this)
          </label>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Inappropriate photo"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isBusy}
          >
            <CircleBackslashIcon className="h-4 w-4" />
            Ban person
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
